import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { motion } from 'framer-motion';
import {
  Fingerprint, ArrowRight, FileText, Eye, DownloadSimple, Buildings, GraduationCap, Rocket, Sparkle
} from '@phosphor-icons/react';
import axios from 'axios';
import { jsPDF } from 'jspdf';
import autoTable from 'jspdf-autotable';
import CognitiveHexagon from './CognitiveHexagon';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Parse inline **bold** markers into React fragments
const renderInline = (text) =>
  String(text).split(/(\*\*[^*]+\*\*)/g).map((part, i) => {
    if (part.startsWith('**') && part.endsWith('**')) {
      return (
        <strong key={i} style={{ color: 'var(--text-primary)', fontWeight: 700 }}>
          {part.slice(2, -2)}
        </strong>
      );
    }
    return <React.Fragment key={i}>{part}</React.Fragment>;
  });

// Render markdown-ish text
const RenderText = ({ text }) => (
  <div className="space-y-3">
    {text.split('\n').map((line, idx) => {
      const t = line.trim();
      if (!t) return <div key={idx} className="h-2" />;
      if (t.startsWith('# ') || t.startsWith('## ') || t.startsWith('### ')) {
        const level = t.match(/^#+/)[0].length;
        const content = t.replace(/^#+\s*/, '');
        if (level <= 2) return <h3 key={idx} className="text-xl font-bold mt-7 mb-2" style={{ color: 'var(--text-primary)', letterSpacing: '-0.01em' }}>{content}</h3>;
        return <h4 key={idx} className="text-sm font-semibold mt-4 mb-1" style={{ color: 'var(--accent-cyan)', letterSpacing: '0.04em' }}>{content}</h4>;
      }
      if (/^\d+\.\s+[A-ZÄÖÜ]/.test(t)) return <h3 key={idx} className="text-lg font-bold mt-6 mb-2" style={{ color: 'var(--accent-cyan)' }}>{t}</h3>;
      if (t.startsWith('**') && t.endsWith('**') && !t.slice(2, -2).includes('**')) return <p key={idx} className="text-sm font-semibold mt-3" style={{ color: 'var(--text-primary)' }}>{t.replace(/\*\*/g, '')}</p>;
      if (t.startsWith('- ') || t.startsWith('* ')) return (
        <div key={idx} className="flex gap-3 pl-2">
          <span className="mt-1 flex-shrink-0" style={{ color: 'var(--accent-cyan)', fontWeight: 700 }}>•</span>
          <p className="text-sm" style={{ color: 'var(--text-secondary)', lineHeight: 1.65, textAlign: 'justify' }}>{renderInline(t.substring(2))}</p>
        </div>
      );
      return <p key={idx} className="text-sm" style={{ color: 'var(--text-secondary)', lineHeight: 1.7, textAlign: 'justify' }}>{renderInline(t)}</p>;
    })}
  </div>
);


const ProjectionPage = () => {
  const { sessionId } = useParams();
  const navigate = useNavigate();
  const [projection, setProjection] = useState(null);
  const [cognitiveType, setCognitiveType] = useState(null);
  const [lifeStage, setLifeStage] = useState(null);
  const [career, setCareer] = useState(null);
  const [deepeningItems, setDeepeningItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [exporting, setExporting] = useState(false);
  const pdfRef = useRef(null);

  const exportPDF = async () => {
    if (!projection) return;
    setExporting(true);
    try {
      const pdf = new jsPDF('p', 'mm', 'a4');
      const pageW = pdf.internal.pageSize.getWidth();
      const pageH = pdf.internal.pageSize.getHeight();
      const marginX = 18;
      const marginY = 20;
      const contentW = pageW - marginX * 2;
      let y = marginY;

      const ensureSpace = (h) => {
        if (y + h > pageH - marginY) {
          pdf.addPage();
          y = marginY;
        }
      };

      const writeText = (text, { size = 10, bold = false, color = [30, 30, 30], gap = 4, lineH = 1.4 } = {}) => {
        if (!text) return;
        pdf.setFont('helvetica', bold ? 'bold' : 'normal');
        pdf.setFontSize(size);
        pdf.setTextColor(color[0], color[1], color[2]);
        const lines = pdf.splitTextToSize(String(text), contentW);
        const lineHeight = size * 0.3528 * lineH;
        lines.forEach((line) => {
          ensureSpace(lineHeight);
          pdf.text(line, marginX, y);
          y += lineHeight;
        });
        y += gap;
      };

      // Inline **bold** aware paragraph renderer with word-wrapping, optional indent + justified alignment (Blocksatz)
      const writeRich = (text, { size = 10, color = [60, 60, 60], gap = 2, lineH = 1.5, indent = 0, justify = true } = {}) => {
        if (!text) return;
        pdf.setFontSize(size);
        pdf.setTextColor(color[0], color[1], color[2]);
        const lineHeight = size * 0.3528 * lineH;
        const startX = marginX + indent;
        const maxW = (pageW - marginX) - startX;
        // Split into bold/normal segments, then collapse into word tokens
        const segments = String(text)
          .split(/(\*\*[^*]+\*\*)/g)
          .filter((s) => s.length > 0)
          .map((s) =>
            s.startsWith('**') && s.endsWith('**')
              ? { text: s.slice(2, -2), bold: true }
              : { text: s, bold: false }
          );
        const words = [];
        segments.forEach((seg) => {
          seg.text.split(/\s+/).forEach((w) => {
            if (w.length) words.push({ t: w, bold: seg.bold });
          });
        });
        if (words.length === 0) return;
        const widthOf = (tok) => {
          pdf.setFont('helvetica', tok.bold ? 'bold' : 'normal');
          return pdf.getTextWidth(tok.t);
        };
        pdf.setFont('helvetica', 'normal');
        const spaceW = pdf.getTextWidth(' ');
        // Greedy line breaking
        const lines = [];
        let cur = [];
        let curW = 0;
        words.forEach((tok) => {
          const w = widthOf(tok);
          const projected = cur.length === 0 ? w : curW + spaceW + w;
          if (cur.length > 0 && projected > maxW) {
            lines.push(cur);
            cur = [tok];
            curW = w;
          } else {
            cur.push(tok);
            curW = projected;
          }
        });
        if (cur.length) lines.push(cur);
        // Render lines (justify all but the last line of the paragraph)
        lines.forEach((lineWords, li) => {
          ensureSpace(lineHeight);
          const isLast = li === lines.length - 1;
          let gapW = spaceW;
          if (justify && !isLast && lineWords.length > 1) {
            const sumW = lineWords.reduce((s, tok) => s + widthOf(tok), 0);
            gapW = (maxW - sumW) / (lineWords.length - 1);
          }
          let x = startX;
          lineWords.forEach((tok, wi) => {
            pdf.setFont('helvetica', tok.bold ? 'bold' : 'normal');
            pdf.text(tok.t, x, y);
            x += widthOf(tok) + (wi < lineWords.length - 1 ? gapW : 0);
          });
          y += lineHeight;
        });
        y += gap;
      };

      const writeDivider = () => {
        ensureSpace(6);
        pdf.setDrawColor(220, 220, 220);
        pdf.setLineWidth(0.3);
        pdf.line(marginX, y, pageW - marginX, y);
        y += 6;
      };

      const writeSkillBar = (label, val) => {
        const rowH = 7;
        ensureSpace(rowH);
        const barW = contentW - 50;
        const barX = marginX + 50;
        const barY = y - 3;
        // Label
        pdf.setFont('helvetica', 'normal');
        pdf.setFontSize(9);
        pdf.setTextColor(60, 60, 60);
        pdf.text(String(label).replace(/_/g, ' '), marginX, y);
        // Bar background
        pdf.setFillColor(230, 230, 230);
        pdf.rect(barX, barY, barW, 3, 'F');
        // Bar fill
        const fillW = (Math.max(0, Math.min(100, val)) / 100) * barW;
        if (val >= 75) pdf.setFillColor(34, 197, 94);
        else if (val >= 55) pdf.setFillColor(245, 158, 11);
        else pdf.setFillColor(91, 184, 196);
        pdf.rect(barX, barY, fillW, 3, 'F');
        // Value
        pdf.setFont('helvetica', 'bold');
        pdf.setFontSize(9);
        pdf.setTextColor(30, 30, 30);
        pdf.text(`${val}%`, pageW - marginX, y, { align: 'right' });
        y += rowH;
      };

      // ===== HEADER =====
      pdf.setFillColor(91, 184, 196);
      pdf.rect(0, 0, pageW, 14, 'F');
      pdf.setFont('helvetica', 'bold');
      pdf.setFontSize(10);
      pdf.setTextColor(255, 255, 255);
      pdf.text('DEEPDIVE · STRATEGISCHE PROJEKTION', marginX, 9);
      const subjectName = projection?.profile?.name || 'Subjekt';
      const genDate = projection.generated_at
        ? new Date(projection.generated_at).toLocaleDateString('de-DE')
        : new Date().toLocaleDateString('de-DE');
      pdf.text(`${subjectName} · ${genDate}`, pageW - marginX, 9, { align: 'right' });

      y = 24;

      // ===== TITLE =====
      writeText('STRATEGISCHE PROJEKTION', { size: 22, bold: true, color: [20, 20, 20], gap: 2 });
      writeText(`Dossier für ${subjectName}`, { size: 11, color: [100, 100, 100], gap: 8 });

      // ===== SCORE =====
      const score = projection.overall_score || 0;
      writeText('GESAMTPOTENZIAL', { size: 9, bold: true, color: [91, 184, 196], gap: 3 });
      // Score bar
      ensureSpace(10);
      pdf.setFillColor(240, 240, 240);
      pdf.rect(marginX, y, contentW, 6, 'F');
      pdf.setFillColor(score >= 75 ? 34 : score >= 50 ? 245 : 91, score >= 75 ? 197 : score >= 50 ? 158 : 184, score >= 75 ? 94 : score >= 50 ? 11 : 196);
      pdf.rect(marginX, y, (score / 100) * contentW, 6, 'F');
      pdf.setFont('helvetica', 'bold');
      pdf.setFontSize(14);
      pdf.setTextColor(20, 20, 20);
      pdf.text(`${score}%`, pageW - marginX, y + 5, { align: 'right' });
      y += 14;

      // ===== SKILLS =====
      if (projection.skill_scores && Object.keys(projection.skill_scores).length > 0) {
        writeDivider();
        writeText('SKILL-PROFIL', { size: 11, bold: true, color: [20, 20, 20], gap: 5 });
        const sorted = Object.entries(projection.skill_scores).sort((a, b) => b[1] - a[1]);
        const staerken = sorted.filter(([, v]) => v >= 55);
        const potenziale = sorted.filter(([, v]) => v < 55);
        if (staerken.length > 0) {
          writeText('STAERKEN', { size: 8, bold: true, color: [34, 197, 94], gap: 3 });
          staerken.forEach(([k, v]) => writeSkillBar(k, v));
          y += 3;
        }
        if (potenziale.length > 0) {
          writeText('POTENZIALE', { size: 8, bold: true, color: [91, 184, 196], gap: 3 });
          potenziale.forEach(([k, v]) => writeSkillBar(k, v));
        }
      }

      // ===== COGNITIVE TYPE =====
      if (cognitiveType) {
        writeDivider();
        writeText('KOGNITIVE MATRIX · 16-TYPEN', { size: 9, bold: true, color: [91, 184, 196], gap: 3 });
        writeText(cognitiveType.deepdive_name || '—', { size: 18, bold: true, color: [20, 20, 20], gap: 1 });
        writeText(`${cognitiveType.mbti_code || ''} · ${cognitiveType.classic_name || ''}`, { size: 9, color: [91, 184, 196], gap: 2 });
        writeText(cognitiveType.tagline || '', { size: 10, bold: true, color: [30, 30, 30], gap: 2 });
        writeText(cognitiveType.description || '', { size: 9, color: [80, 80, 80], gap: 4 });
        // 4 dimensions as a styled autoTable
        const poleNames = {
          E: 'Extraversion', I: 'Introversion',
          S: 'Sensing', N: 'Intuition',
          T: 'Thinking', F: 'Feeling',
          J: 'Judging', P: 'Perceiving',
        };
        const axes = [
          { key: 'E_I', label: 'Interaktion', l: 'E', r: 'I' },
          { key: 'S_N', label: 'Info-Fluss', l: 'S', r: 'N' },
          { key: 'T_F', label: 'Logik', l: 'T', r: 'F' },
          { key: 'J_P', label: 'Struktur', l: 'J', r: 'P' },
        ];
        const scores = cognitiveType.scores || {};
        const matrixRows = axes.map((a) => {
          const sc = Math.max(0, Math.min(100, scores[a.key] ?? 50));
          const dominant = sc >= 50 ? a.r : a.l;
          const dominantPct = Math.max(sc, 100 - sc);
          return [
            a.label,
            `${poleNames[a.l]} (${a.l}) ${100 - sc}%`,
            `${poleNames[a.r]} (${a.r}) ${sc}%`,
            `${poleNames[dominant]} · ${dominantPct}%`,
          ];
        });
        autoTable(pdf, {
          startY: y,
          head: [['Dimension', 'Linker Pol', 'Rechter Pol', 'Auspraegung']],
          body: matrixRows,
          theme: 'grid',
          margin: { left: marginX, right: marginX },
          styles: { font: 'helvetica', fontSize: 8, cellPadding: 2, textColor: [60, 60, 60], lineColor: [225, 225, 225] },
          headStyles: { fillColor: [91, 184, 196], textColor: [255, 255, 255], fontStyle: 'bold', fontSize: 8 },
          columnStyles: { 3: { fontStyle: 'bold', textColor: [30, 30, 30] } },
        });
        y = pdf.lastAutoTable.finalY + 6;
      }

      // ===== PROJECTION TEXT =====
      const projText = projection.projection_text || '';
      if (projText) {
        writeDivider();
        const lines = projText.split('\n');
        for (const raw of lines) {
          const t = raw.trim();
          if (!t) { y += 2; continue; }
          if (t.startsWith('# ') || t.startsWith('## ')) {
            writeText(t.replace(/^#+\s*/, '').replace(/\*\*/g, ''), { size: 14, bold: true, color: [20, 20, 20], gap: 3 });
          } else if (t.startsWith('### ')) {
            writeText(t.replace(/^#+\s*/, '').replace(/\*\*/g, ''), { size: 11, bold: true, color: [91, 184, 196], gap: 2 });
          } else if (/^\d+\.\s+[A-ZÄÖÜ]/.test(t)) {
            writeText(t.replace(/\*\*/g, ''), { size: 12, bold: true, color: [91, 184, 196], gap: 3 });
          } else if ((t.startsWith('**') && t.endsWith('**')) && !t.slice(2, -2).includes('**')) {
            writeText(t.replace(/\*\*/g, ''), { size: 10, bold: true, color: [20, 20, 20], gap: 2 });
          } else if (t.startsWith('- ') || t.startsWith('* ')) {
            ensureSpace(5);
            pdf.setFont('helvetica', 'bold');
            pdf.setFontSize(10);
            pdf.setTextColor(91, 184, 196);
            pdf.text('•', marginX + 2, y);
            writeRich(t.substring(2), { size: 10, color: [60, 60, 60], gap: 1, indent: 7 });
          } else {
            writeRich(t, { size: 10, color: [60, 60, 60], gap: 2 });
          }
        }
      }

      // ===== DEINE EIGENEN WORTE (KI-Vertiefungen) =====
      if (deepeningItems && deepeningItems.length > 0) {
        writeDivider();
        writeText('Deine eigenen Worte', { size: 14, bold: true, color: [20, 20, 20], gap: 2 });
        writeText('Als persoenliches Zeit-Archiv deiner Session.', { size: 9, color: [140, 140, 140], gap: 4 });
        const blockLabels = {
          hidden_talent: 'Hidden Talent Scan',
          geist: 'Geist', koerper: 'Koerper', wesen: 'Wesen',
          biografie: 'Biografie', kompass: 'Kompass'
        };
        for (const item of deepeningItems) {
          ensureSpace(18);
          writeText((blockLabels[item.block_id] || item.block_id).toUpperCase(),
            { size: 9, bold: true, color: [91, 184, 196], gap: 2 });
          writeText('„' + (item.question || '') + '"',
            { size: 10, color: [30, 30, 30], gap: 2, lineH: 1.4 });
          writeText(item.answer || '',
            { size: 10, color: [70, 70, 70], gap: 5, lineH: 1.5 });
        }
      }

      // ===== FOOTER on each page =====
      const pageCount = pdf.internal.getNumberOfPages();
      for (let i = 1; i <= pageCount; i++) {
        pdf.setPage(i);
        pdf.setDrawColor(220, 220, 220);
        pdf.setLineWidth(0.2);
        pdf.line(marginX, pageH - 12, pageW - marginX, pageH - 12);
        pdf.setFont('helvetica', 'normal');
        pdf.setFontSize(8);
        pdf.setTextColor(140, 140, 140);
        pdf.text(`DEEPDIVE · ${subjectName}`, marginX, pageH - 7);
        pdf.text(`Seite ${i} / ${pageCount}`, pageW - marginX, pageH - 7, { align: 'right' });
      }

      const safeName = subjectName.replace(/[^a-zA-Z0-9_-]/g, '_');
      const dateStr = new Date().toISOString().split('T')[0];
      const filename = `DEEPDIVE_Projektion_${safeName}_${dateStr}.pdf`;

      // Explicit blob-based download (works reliably even after async gap)
      const blob = pdf.output('blob');
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = filename;
      a.rel = 'noopener';
      document.body.appendChild(a);
      a.click();
      setTimeout(() => {
        document.body.removeChild(a);
        URL.revokeObjectURL(url);
      }, 100);
    } catch (err) {
      console.error('PDF export error:', err);
      alert('PDF-Export fehlgeschlagen: ' + (err?.message || 'Unbekannter Fehler'));
    } finally {
      setExporting(false);
    }
  };

  useEffect(() => {
    const fetchAll = async () => {
      try {
        const [projRes, cogRes, statusRes, careerRes, deepRes] = await Promise.all([
          axios.get(`${API}/user/projection/${sessionId}`),
          axios.get(`${API}/user/cognitive-matrix/${sessionId}`).catch(() => ({ data: { cognitive_type: null } })),
          axios.get(`${API}/user/status/${sessionId}`).catch(() => ({ data: {} })),
          axios.get(`${API}/user/career-suggestions/${sessionId}`).catch(() => ({ data: null })),
          axios.get(`${API}/blocks/deepening/${sessionId}`).catch(() => ({ data: { items: [] } })),
        ]);
        setProjection(projRes.data);
        setCognitiveType(cogRes?.data?.cognitive_type || null);
        setLifeStage(statusRes?.data?.life_stage || null);
        if (careerRes?.data?.status === 'done') {
          setCareer(careerRes.data.suggestions);
        }
        const items = (deepRes?.data?.items || []).filter(i => (i.answer || '').trim().length > 0);
        setDeepeningItems(items);
      } catch (err) {
        console.error('Error:', err);
      } finally {
        setLoading(false);
      }
    };
    fetchAll();
  }, [sessionId]);

  if (loading) {
    return (
      <div className="page-shell-bg flex items-center justify-center">
        <motion.div animate={{ rotate: 360 }} transition={{ duration: 2, repeat: Infinity, ease: 'linear' }}>
          <Fingerprint size={60} style={{ color: 'var(--accent-cyan)' }} />
        </motion.div>
      </div>
    );
  }

  if (!projection) {
    return (
      <div className="page-shell-bg flex items-center justify-center">
        <div className="page-shell-card p-10 text-center max-w-md">
          <Eye size={56} className="mx-auto mb-4" style={{ color: 'var(--accent-cyan)' }} />
          <h1 className="text-2xl font-bold mb-3">Projektion nicht gefunden</h1>
          <p className="text-sm mb-5" style={{ color: 'var(--text-secondary)' }}>
            Wir konnten kein Dossier für diese Session laden.
          </p>
          <button onClick={() => navigate(`/dashboard/${sessionId}`)} className="page-shell-btn">
            Zurueck zum Dashboard
          </button>
        </div>
      </div>
    );
  }

  const text = projection.projection_text || '';
  const score = projection.overall_score || 0;
  const scoreColor = score >= 75 ? '#16A34A' : score >= 50 ? '#F59E0B' : 'var(--accent-cyan)';
  const generatedDate = projection.generated_at ? new Date(projection.generated_at).toLocaleDateString('de-DE') : '';

  return (
    <div className="page-shell-bg" style={{ alignItems: 'flex-start', paddingTop: '1.5rem', paddingBottom: '4rem' }} data-testid="projection-page">
      <motion.div
        initial={{ scale: 0.96, opacity: 0, y: 20 }}
        animate={{ scale: 1, opacity: 1, y: 0 }}
        transition={{ type: 'spring', damping: 25, stiffness: 300 }}
        className="w-full max-w-4xl page-shell-card"
      >
        {/* Header */}
        <div className="page-shell-header">
          <div className="flex items-center gap-3">
            <div className="hex-clip flex items-center justify-center" style={{ width: 40, height: 40, background: 'var(--accent-cyan)' }}>
              <Fingerprint size={20} weight="bold" color="#fff" />
            </div>
            <div>
              <p className="text-xs font-semibold" style={{ color: 'var(--text-secondary)', letterSpacing: '0.08em' }}>DEEPDIVE · DOSSIER</p>
              <h2 className="text-lg font-bold">Strategische Projektion</h2>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={exportPDF}
              disabled={exporting}
              className="flex items-center gap-2 px-4 py-2 text-xs font-semibold transition-all hover:scale-[1.03]"
              style={{
                background: exporting ? 'rgba(91,184,196,0.15)' : 'var(--accent-cyan)',
                color: exporting ? 'var(--accent-cyan)' : '#fff',
                border: 'none',
                borderRadius: '10px',
                cursor: exporting ? 'wait' : 'pointer',
                boxShadow: exporting ? 'none' : '0 4px 14px rgba(91,184,196,0.3)'
              }}
              data-testid="export-pdf-btn"
            >
              <DownloadSimple size={14} weight="bold" />
              {exporting ? 'Exportiert…' : 'PDF Export'}
            </button>
            <span className="text-xs px-3 py-1.5 font-semibold hidden sm:inline-block"
              style={{ border: '1px solid var(--border-default)', borderRadius: '9999px', color: 'var(--text-secondary)' }}>
              {projection.profile?.name || 'Subjekt'}
            </span>
          </div>
        </div>

        {/* Body */}
        <div className="page-shell-body" ref={pdfRef}>

          {/* Score Card */}
          <div className="mb-6 p-5" style={{
            background: `linear-gradient(135deg, ${scoreColor}0A 0%, ${scoreColor}05 100%)`,
            border: `1px solid ${scoreColor}20`,
            borderRadius: '16px'
          }}>
            <div className="flex justify-between items-baseline mb-3">
              <div>
                <p className="text-xs font-semibold" style={{ color: 'var(--text-secondary)', letterSpacing: '0.08em' }}>GESAMTPOTENZIAL</p>
                <p className="text-xs mt-0.5" style={{ color: 'var(--text-secondary)' }}>Generiert am {generatedDate}</p>
                {lifeStage && (
                  <p className="text-xs mt-1 font-semibold" data-testid="dossier-life-stage">
                    <span style={{ color: 'var(--accent-cyan)', letterSpacing: '0.08em' }}>
                      STATUS · {lifeStage.name.toUpperCase()}
                    </span>
                    <span style={{ color: 'var(--text-secondary)' }}> · {lifeStage.focus}</span>
                  </p>
                )}
              </div>
              <p className="text-4xl font-black" style={{ color: scoreColor, letterSpacing: '-0.02em' }}>
                {score}%
              </p>
            </div>
            <div className="w-full h-2.5" style={{ background: 'var(--border-default)', borderRadius: '9999px', overflow: 'hidden' }}>
              <motion.div initial={{ width: 0 }} animate={{ width: `${score}%` }} transition={{ duration: 1.5, ease: 'easeOut' }}
                className="h-full" style={{ background: scoreColor, borderRadius: '9999px' }} />
            </div>
          </div>

          {/* Cognitive Type Card + Hexagon */}
          {cognitiveType && (
            <div className="mb-6 p-5" style={{
              background: 'linear-gradient(135deg, rgba(91,184,196,0.1) 0%, rgba(91,184,196,0.02) 100%)',
              border: '1px solid rgba(91,184,196,0.35)',
              borderRadius: '18px'
            }} data-testid="dossier-cognitive-type">
              <div className="flex items-start justify-between gap-3 mb-4">
                <div>
                  <p className="text-xs font-semibold" style={{ color: 'var(--accent-cyan)', letterSpacing: '0.1em' }}>BETRIEBSSYSTEM · 16-TYPEN</p>
                  <p className="text-2xl font-black mt-1" style={{ color: 'var(--text-primary)', letterSpacing: '-0.02em' }}>
                    {cognitiveType.deepdive_name}
                  </p>
                  <p className="text-xs font-mono mt-0.5" style={{ color: 'var(--accent-cyan)', letterSpacing: '0.15em' }}>
                    {cognitiveType.mbti_code} · {cognitiveType.classic_name}
                  </p>
                  <p className="text-sm font-semibold mt-2" style={{ color: 'var(--text-primary)' }}>
                    {cognitiveType.tagline}
                  </p>
                </div>
              </div>
              <p className="text-xs mb-4" style={{ color: 'var(--text-secondary)', lineHeight: 1.6 }}>
                {cognitiveType.description}
              </p>
              <CognitiveHexagon type={cognitiveType} />
            </div>
          )}

          {/* Skill Bars */}
          {projection.skill_scores && Object.keys(projection.skill_scores).length > 0 && (() => {
            const sorted = Object.entries(projection.skill_scores).sort((a, b) => b[1] - a[1]);
            const staerken = sorted.filter(([, val]) => val >= 55);
            const potenziale = sorted.filter(([, val]) => val < 55);
            const renderSkill = ([skill, val], idx, accent) => (
              <div key={skill} className="mb-3.5">
                <div className="flex justify-between mb-1.5">
                  <span className="text-sm" style={{ color: 'var(--text-primary)', textTransform: 'capitalize' }}>
                    {skill.replace(/_/g, ' ')}
                  </span>
                  <span className="text-sm font-bold" style={{ color: accent }}>
                    {val}%
                  </span>
                </div>
                <div className="w-full h-2" style={{ background: 'var(--border-default)', borderRadius: '9999px', overflow: 'hidden' }}>
                  <motion.div initial={{ width: 0 }} animate={{ width: `${val}%` }}
                    transition={{ duration: 1, delay: 0.1 * idx, ease: 'easeOut' }}
                    className="h-full" style={{ background: accent, borderRadius: '9999px' }} />
                </div>
              </div>
            );
            return (
              <div className="mb-8 p-5" style={{ background: 'rgba(0,0,0,0.02)', borderRadius: '16px', border: '1px solid var(--border-default)' }}>
                <p className="text-xs font-semibold mb-4" style={{ color: 'var(--text-secondary)', letterSpacing: '0.08em' }}>SKILL-PROFIL</p>

                {staerken.length > 0 && (
                  <div className="mb-5">
                    <p className="text-xs font-bold mb-3" style={{ color: '#16A34A', letterSpacing: '0.1em' }}>STAERKEN</p>
                    {staerken.map((item, i) => renderSkill(item, i, item[1] >= 75 ? '#16A34A' : '#F59E0B'))}
                  </div>
                )}

                {potenziale.length > 0 && (
                  <div>
                    {staerken.length > 0 && <div className="mb-4" style={{ borderTop: '1px solid var(--border-default)' }} />}
                    <p className="text-xs font-bold mb-3" style={{ color: 'var(--accent-cyan)', letterSpacing: '0.1em' }}>POTENZIALE</p>
                    {potenziale.map((item, i) => renderSkill(item, staerken.length + i, 'var(--accent-cyan)'))}
                  </div>
                )}
              </div>
            );
          })()}

          {/* Projection Text */}
          <div className="projection-content" data-testid="projection-text" style={{ color: 'var(--text-primary)' }}>
            <RenderText text={text} />
          </div>

          {/* === Deine eigenen Worte (KI-Vertiefungen — Selbst-Zeit-Kapsel) === */}
          {deepeningItems.length > 0 && (
            <div className="mt-8 pt-6" style={{ borderTop: '1px solid var(--border-default)' }} data-testid="dossier-own-words">
              <div className="flex items-center gap-3 mb-2">
                <Sparkle size={20} weight="duotone" style={{ color: 'var(--accent-cyan)' }} />
                <h3 className="text-xl font-bold" style={{ color: 'var(--text-primary)' }}>Deine eigenen Worte</h3>
              </div>
              <p className="text-sm mb-5" style={{ color: 'var(--text-secondary)', lineHeight: 1.6 }}>
                Wenn du dieses Dossier in einem Jahr wieder liest — das hier hast du heute gesagt.
                Als persönliches Zeit-Archiv deiner Session, festgehalten am {generatedDate}.
              </p>
              <div className="space-y-4">
                {deepeningItems.map((item, idx) => {
                  const blockLabels = {
                    hidden_talent: 'Hidden Talent Scan',
                    geist: 'Geist', koerper: 'Körper', wesen: 'Wesen',
                    biografie: 'Biografie', kompass: 'Kompass'
                  };
                  return (
                    <div key={idx} className="p-4" style={{
                      background: 'rgba(91,184,196,0.05)',
                      border: '1px solid rgba(91,184,196,0.2)',
                      borderRadius: '14px',
                    }}>
                      <p className="text-xs font-semibold mb-2" style={{ color: 'var(--accent-cyan)', letterSpacing: '0.1em' }}>
                        {(blockLabels[item.block_id] || item.block_id).toUpperCase()}
                      </p>
                      <p className="text-sm mb-3" style={{ color: 'var(--text-primary)', lineHeight: 1.5, fontStyle: 'italic' }}>
                        „{item.question}"
                      </p>
                      <p className="text-sm" style={{ color: 'var(--text-secondary)', lineHeight: 1.6, paddingLeft: 12, borderLeft: '2px solid var(--accent-cyan)' }}>
                        {item.answer}
                      </p>
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {/* === Berufliche Richtungen (integriert aus Career Navigator) === */}
          {career && (career.jobs?.length || career.studium?.length || career.selbstaendig?.length) > 0 && (
            <div className="mt-8 pt-6" style={{ borderTop: '1px solid var(--border-default)' }} data-testid="dossier-career-suggestions">
              <div className="flex items-center gap-3 mb-2">
                <Sparkle size={20} weight="duotone" style={{ color: 'var(--accent-cyan)' }} />
                <h3 className="text-xl font-bold" style={{ color: 'var(--text-primary)' }}>Berufliche Richtungen</h3>
              </div>
              <p className="text-sm mb-5" style={{ color: 'var(--text-secondary)', lineHeight: 1.6 }}>
                Aus deinem Profil ergeben sich mehrere Wege, die zu dir passen koennten. Sie sind als Inspiration gedacht — eine grobe Karte, kein Auftrag. Schau, welche Richtung in dir resoniert.
              </p>

              {/* Jobs */}
              {career.jobs && career.jobs.length > 0 && (
                <div className="mb-5">
                  <div className="flex items-center gap-2 mb-3">
                    <Buildings size={16} weight="duotone" style={{ color: 'var(--accent-cyan)' }} />
                    <p className="text-xs font-semibold" style={{ color: 'var(--accent-cyan)', letterSpacing: '0.1em' }}>JOBS · ALS ANGESTELLTER</p>
                  </div>
                  {career.jobs.slice(0, 3).map((j, i) => (
                    <div key={i} className="mb-3 p-4" style={{ background: 'var(--bg-surface)', border: '1px solid var(--border-default)', borderRadius: '14px', borderLeft: '3px solid var(--accent-cyan)' }}>
                      <div className="flex justify-between items-baseline gap-3 mb-1">
                        <p className="text-sm font-bold" style={{ color: 'var(--text-primary)' }}>{j.titel}</p>
                        <span className="text-xs font-bold flex-shrink-0" style={{ color: 'var(--accent-cyan)' }}>{j.match_score}%</span>
                      </div>
                      <p className="text-xs mb-2" style={{ color: 'var(--text-secondary)' }}>{j.branche}</p>
                      <p className="text-xs" style={{ color: 'var(--text-primary)', lineHeight: 1.55 }}>{j.begruendung}</p>
                      {j.einstieg && (
                        <p className="text-xs mt-2" style={{ color: 'var(--text-secondary)' }}>
                          <span className="font-semibold" style={{ color: 'var(--accent-cyan)' }}>Erster Schritt: </span>{j.einstieg}
                        </p>
                      )}
                    </div>
                  ))}
                </div>
              )}

              {/* Studium */}
              {career.studium && career.studium.length > 0 && (
                <div className="mb-5">
                  <div className="flex items-center gap-2 mb-3">
                    <GraduationCap size={16} weight="duotone" style={{ color: '#A855F7' }} />
                    <p className="text-xs font-semibold" style={{ color: '#A855F7', letterSpacing: '0.1em' }}>WEITERBILDUNG · STUDIUM</p>
                  </div>
                  {career.studium.slice(0, 2).map((s, i) => (
                    <div key={i} className="mb-3 p-4" style={{ background: 'var(--bg-surface)', border: '1px solid var(--border-default)', borderRadius: '14px', borderLeft: '3px solid #A855F7' }}>
                      <div className="flex justify-between items-baseline gap-3 mb-1">
                        <p className="text-sm font-bold" style={{ color: 'var(--text-primary)' }}>{s.titel}</p>
                        <span className="text-xs font-bold flex-shrink-0" style={{ color: '#A855F7' }}>{s.match_score}%</span>
                      </div>
                      <p className="text-xs mb-2" style={{ color: 'var(--text-secondary)' }}>{s.typ} · {s.dauer}</p>
                      <p className="text-xs" style={{ color: 'var(--text-primary)', lineHeight: 1.55 }}>{s.begruendung}</p>
                    </div>
                  ))}
                </div>
              )}

              {/* Selbstaendigkeit */}
              {career.selbstaendig && career.selbstaendig.length > 0 && (
                <div className="mb-2">
                  <div className="flex items-center gap-2 mb-3">
                    <Rocket size={16} weight="duotone" style={{ color: '#F59E0B' }} />
                    <p className="text-xs font-semibold" style={{ color: '#F59E0B', letterSpacing: '0.1em' }}>SELBSTSTAENDIG · GRUENDUNG</p>
                  </div>
                  {career.selbstaendig.slice(0, 2).map((u, i) => (
                    <div key={i} className="mb-3 p-4" style={{ background: 'var(--bg-surface)', border: '1px solid var(--border-default)', borderRadius: '14px', borderLeft: '3px solid #F59E0B' }}>
                      <div className="flex justify-between items-baseline gap-3 mb-1">
                        <p className="text-sm font-bold" style={{ color: 'var(--text-primary)' }}>{u.titel}</p>
                        <span className="text-xs font-bold flex-shrink-0" style={{ color: '#F59E0B' }}>{u.match_score}%</span>
                      </div>
                      <p className="text-xs mb-2" style={{ color: 'var(--text-secondary)' }}>{u.geschaeftsmodell}</p>
                      <p className="text-xs" style={{ color: 'var(--text-primary)', lineHeight: 1.55 }}>{u.begruendung}</p>
                      {u.erster_schritt && (
                        <p className="text-xs mt-2" style={{ color: 'var(--text-secondary)' }}>
                          <span className="font-semibold" style={{ color: '#F59E0B' }}>Erster Schritt: </span>{u.erster_schritt}
                        </p>
                      )}
                    </div>
                  ))}
                </div>
              )}

              <p className="text-xs italic mt-4" style={{ color: 'var(--text-secondary)', lineHeight: 1.6 }}>
                Diese Richtungen sind ein Spiegel deiner aktuellen Faehigkeiten und Werte — nicht deine Zukunft.
                Pruefe sie mit dem Kompass, den du hier entwickelt hast.
              </p>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 px-6 py-5"
          style={{ borderTop: '1px solid var(--border-default)' }}>
          <div className="flex items-center gap-2.5">
            <FileText size={16} style={{ color: 'var(--text-secondary)' }} />
            <p className="text-xs" style={{ color: 'var(--text-secondary)' }}>
              Generiert am {generatedDate} · Gemini Flash
            </p>
          </div>
          <button onClick={() => navigate(`/dashboard/${sessionId}`)}
            className="page-shell-btn flex items-center gap-2"
            style={{ width: 'auto', padding: '0.625rem 1.25rem' }}
            data-testid="projection-to-dashboard">
            Zurueck zum Dashboard
            <ArrowRight size={16} weight="bold" />
          </button>
        </div>
      </motion.div>
    </div>
  );
};

export default ProjectionPage;
